<template>
    <div class="public-bracket-app container is-fluid" :class="{ 'is-clipped': embedded }">
        <!-- Embedded, the root has no side padding, so the -0.75rem margins of `.columns` would overflow it and make the modal scroll sideways. -->
        <div class="columns" v-if="!embedded">
            <div class="column has-text-centered is-full">
                <h1 class="title is-size-5-mobile mb-2-mobile">{{ eventFullName }}</h1>
                <h2 class="subtitle is-size-6-mobile mb-0-mobile">
                    {{ translate("page.public_brackets.title") }}
                </h2>
            </div>
        </div>

        <template v-if="initDone && bracketNotFound">
            <BracketGraphUnavailable />
        </template>

        <template v-else-if="initDone && bracket">
            <!-- On mobile the button sits above the header, so it cannot push the bracket below the fold. -->
            <div class="columns back-button is-hidden-tablet" v-if="!embedded">
                <div class="column is-full pb-0">
                    <a :href="getBackUrl()">
                        <button class="button is-small">
                            <span class="icon">
                                <i class="fa fa-list" />
                            </span>
                            <span>
                                {{ translate("common.go_back_to_list") }}
                            </span>
                        </button>
                    </a>
                </div>
            </div>

            <div class="columns">
                <div class="column is-full">
                    <PublicBracketHeader :bracket="bracket" :event-timezone="eventTimezone" />
                </div>
            </div>

            <!-- Desktop: back button left, ranking jump link right, on one row. Mobile has its own back button above the header, so the row only carries the link there. -->
            <div v-if="!embedded || rankingVisible" class="columns back-button" :class="{ 'is-hidden-mobile': !rankingVisible }">
                <div class="column is-full is-flex is-align-items-center is-justify-content-space-between">
                    <a v-if="!embedded" class="is-hidden-mobile" :href="getBackUrl()">
                        <button class="button">
                            <span class="icon">
                                <i class="fa fa-list" />
                            </span>
                            <span>
                                {{ translate("common.go_back_to_list") }}
                            </span>
                        </button>
                    </a>
                    <a v-if="rankingVisible" class="is-size-7 ml-auto" href="#public-bracket-ranking" @click.prevent="scrollToRanking">
                        <span class="icon is-small"><i class="fas fa-arrow-down" aria-hidden="true"></i></span>
                        <span>{{ translate("page.public_brackets.jump_to_ranking") }}</span>
                    </a>
                </div>
            </div>

            <div class="columns">
                <div class="column is-full">
                    <div class="scrollbox-y">
                        <RoundRobinGraphBracket
                            v-if="bracket.type === BracketTypeEnum.RoundRobin"
                            :key="`rr-${eventId}-${parameterId}`"
                            :rounds="rounds"
                            :embedded="embedded"
                        />
                        <BestOfThreeGraphBracket
                            v-else-if="bracket.type === BracketTypeEnum.BestOfThree"
                            :key="`bo3-${eventId}-${parameterId}`"
                            :rounds="rounds"
                            :embedded="embedded"
                        />
                        <ThreeCompetitorRepechageGraphBracket
                            v-else-if="bracket.type === BracketTypeEnum.ThreeCompetitorRepechage"
                            :key="`tcr-${eventId}-${parameterId}`"
                            :rounds="rounds"
                            :embedded="embedded"
                        />
                        <TwoPoolGraphBracket
                            v-else-if="
                                bracket.type === BracketTypeEnum.TwoPoolsTwo3rdPlaces ||
                                bracket.type === BracketTypeEnum.TwoPoolsOne3rdPlace
                            "
                            :key="`tp-${eventId}-${parameterId}`"
                            :pool1="rounds"
                            :pool2="defeatedRounds"
                            :section3="rounds3"
                            :section4="rounds4"
                            :one-third-place="bracket.type === BracketTypeEnum.TwoPoolsOne3rdPlace"
                            :embedded="embedded"
                        />
                        <KataBracket v-else-if="bracket.type === BracketTypeEnum.Kata" :entries="kataEntries" />
                        <DoubleEliminationGraphBracket
                            v-else-if="
                                bracket.type === BracketTypeEnum.DoubleEliminationOne3rdPlace ||
                                bracket.type === BracketTypeEnum.DoubleEliminationTwo3rdPlaces
                            "
                            :key="`de-${eventId}-${parameterId}`"
                            :winner-rounds="rounds"
                            :defeated-matches="defeatedMatches"
                            :embedded="embedded"
                        />
                        <RepechageByFinalistsGraphBracket
                            v-else-if="bracket.type === BracketTypeEnum.RepechageByFinalists"
                            :key="`rbf-${eventId}-${parameterId}`"
                            :winner-rounds="rounds"
                            :repechage-matches="defeatedMatches"
                            :embedded="embedded"
                        />
                        <SingleEliminationGraphBracket
                            v-else-if="
                                bracket.type === BracketTypeEnum.SingleElimination ||
                                bracket.type === BracketTypeEnum.SingleElimination3rdPlacePlayoff
                            "
                            :key="`se-${eventId}-${parameterId}`"
                            :rounds="rounds"
                            :third-place-playoff="bracket.type === BracketTypeEnum.SingleElimination3rdPlacePlayoff"
                            :embedded="embedded"
                        />
                    </div>
                </div>
            </div>

            <PublicBracketRankingSnapshot ref="rankingSnapshot" :bracket="bracket" class="mt-5" />
        </template>
        <div v-else>
            <div class="columns is-mobile is-centered">
                <LoadingSpinner2 class="has-added-margin" />
            </div>
        </div>

        <RecordProfileModal ref="recordProfileModal" />
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, onMounted, onUnmounted, watch, provide, defineAsyncComponent, type AsyncComponentLoader } from "vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import RecordProfileModal from "../RecordProfile/RecordProfileModal.vue";
    import { eventBus, OPEN_RECORD_PROFILE } from "./publicBracketEventBus";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import HttpError from "../Api/Utils/HttpError";
    import bracketsApi, { PublicBracket } from "../Api/brackets";
    import { BracketTypeEnum } from "../Common/types";
    import KataBracket from "./KataBracket.vue";
    import PublicBracketHeader from "./PublicBracketHeader.vue";
    import PublicBracketRankingSnapshot from "./PublicBracketRankingSnapshot.vue";
    import BracketGraphLoading from "./BracketGraph/BracketGraphLoading.vue";
    import BracketGraphUnavailable from "./BracketGraph/BracketGraphUnavailable.vue";
    import type { BracketRound, BracketSuccessorMatch } from "./BracketGraph/types";
    import type { KataEntry } from "../Common/Kata/useKataScores";
    import { useScrollResetOnMount } from "../Common/useScrollResetOnMount";
    // Async so @vue-flow/core and its d3 dependencies (~85 KB gzip) are split into
    // their own chunk, fetched only when a graph-based bracket is actually rendered,
    // instead of weighing down the shared panelUser bundle on every user-facing page.
    // Each renders BracketGraphLoading (delay 0) while its chunk loads, so the frame
    // reserves its final height up front and the ranking table below does not shift.
    const graphBracket = (loader: AsyncComponentLoader) =>
        defineAsyncComponent({
            loader,
            loadingComponent: BracketGraphLoading,
            delay: 0,
            // A failed chunk fetch (flaky network, stale deploy) would otherwise leave
            // BracketGraphLoading spinning forever; show the same notice the adapters use
            // when they cannot render a payload.
            errorComponent: BracketGraphUnavailable,
            timeout: 20000,
        });

    const SingleEliminationGraphBracket = graphBracket(() => import("./SingleEliminationGraphBracket.vue"));
    const DoubleEliminationGraphBracket = graphBracket(() => import("./DoubleEliminationGraphBracket.vue"));
    const RepechageByFinalistsGraphBracket = graphBracket(() => import("./RepechageByFinalistsGraphBracket.vue"));
    const ThreeCompetitorRepechageGraphBracket = graphBracket(() => import("./ThreeCompetitorRepechageGraphBracket.vue"));
    const RoundRobinGraphBracket = graphBracket(() => import("./RoundRobinGraphBracket.vue"));
    const BestOfThreeGraphBracket = graphBracket(() => import("./BestOfThreeGraphBracket.vue"));
    const TwoPoolGraphBracket = graphBracket(() => import("./TwoPoolGraphBracket.vue"));

    const props = withDefaults(
        defineProps<{
            defaultBackUrl?: string;
            backUrl?: string;
            eventFullName?: string;
            eventTimezone?: string;
            eventId: number;
            parameterId: number;
            embedded?: boolean;
        }>(),
        {
            defaultBackUrl: "",
            backUrl: "",
            eventFullName: "",
            eventTimezone: "UTC",
            embedded: false,
        },
    );

    // Consumed by BracketGraphLoading, which defineAsyncComponent renders without props.
    provide("bracketEmbedded", props.embedded);

    // Only reset scroll on the full public bracket page. This component is also reused
    // embedded inside sidebars (AdminCategorySidebar/PublicCategorySidebar), where hijacking
    // the surrounding page's scroll on open would be a regression, not a fix.
    if (!props.embedded) {
        useScrollResetOnMount();
    }

    const rounds = ref<BracketRound[]>([]);
    const defeatedRounds = ref<BracketRound[]>([]);
    const rounds3 = ref<BracketRound[]>([]);
    const rounds4 = ref<BracketRound[]>([]);
    // results[1] for double elimination and repechage-by-finalists is an explicit id/next
    // successor ladder rather than a list of rounds.
    const defeatedMatches = ref<BracketSuccessorMatch[]>([]);
    const kataEntries = ref<KataEntry[]>([]);
    const initDone = ref(false);
    const bracket = ref<PublicBracket | null>(null);
    const bracketNotFound = ref(false);

    function fetchBracketData() {
        return bracketsApi.getPublicBracketData(props.eventId, props.parameterId).then(bracketData => {
            bracket.value = bracketData;
        });
    }

    function loadBracket() {
        initDone.value = false;
        bracket.value = null;
        bracketNotFound.value = false;

        return fetchBracketData()
            .then(() => {
                const data = bracket.value;
                if (!data) {
                    return;
                }

                rounds.value = [];
                defeatedRounds.value = [];
                rounds3.value = [];
                rounds4.value = [];
                defeatedMatches.value = [];
                kataEntries.value = [];

                switch (data.type) {
                    case BracketTypeEnum.Kata:
                        kataEntries.value = data.results[0];
                        break;
                    case BracketTypeEnum.DoubleEliminationOne3rdPlace:
                    case BracketTypeEnum.DoubleEliminationTwo3rdPlaces:
                    case BracketTypeEnum.RepechageByFinalists:
                        rounds.value = data.results[0];
                        defeatedMatches.value = data.results[1];
                        break;
                    case BracketTypeEnum.TwoPoolsOne3rdPlace:
                    case BracketTypeEnum.TwoPoolsTwo3rdPlaces:
                        rounds.value = data.results[0] ?? [];
                        defeatedRounds.value = data.results[1] ?? [];
                        rounds3.value = data.results[2] ?? [];
                        rounds4.value = data.results[3] ?? [];
                        break;
                    default:
                        rounds.value = data.results[0];
                }
            })
            .catch((err: HttpError) => {
                if (err.statusCode === 404) {
                    bracketNotFound.value = true;
                    return;
                }

                handleError(err);
            })
            .finally(() => {
                initDone.value = true;
            });
    }

    function getBackUrl() {
        if (props.backUrl.length > 0) {
            return props.backUrl;
        }
        return props.defaultBackUrl;
    }

    const rankingSnapshot = ref<InstanceType<typeof PublicBracketRankingSnapshot> | null>(null);

    const rankingVisible = computed(() => rankingSnapshot.value?.showRankingTable === true);

    function scrollToRanking() {
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        document.getElementById("public-bracket-ranking")?.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
    }

    const recordProfileModal = ref<InstanceType<typeof RecordProfileModal> | null>(null);

    function openRecordProfile(eventId, publicId) {
        recordProfileModal.value?.open(eventId, publicId);
    }

    function handleOpenRecordProfile({ publicId }: { publicId: string }) {
        openRecordProfile(props.eventId, publicId);
    }

    onMounted(() => {
        eventBus.$on(OPEN_RECORD_PROFILE, handleOpenRecordProfile);

        return loadBracket();
    });

    onUnmounted(() => {
        eventBus.$off(OPEN_RECORD_PROFILE, handleOpenRecordProfile);
    });

    watch(
        () => [props.eventId, props.parameterId],
        () => {
            loadBracket();
        },
    );
</script>
